import React, { useState, useContext, useEffect } from 'react';
import {
    Button,
    Space,
    Row,
    Col,
    Modal,
    Popconfirm,
    Divider,
    Tag,
    List,
    Alert,
    message,
    Card,
    Tabs,
    Badge
} from 'antd';
import {
    EyeOutlined,
    DeleteOutlined,
    EditOutlined,
    CheckOutlined,
    FilePdfOutlined,
    SendOutlined,
    CloseCircleOutlined,
    CommentOutlined
} from '@ant-design/icons';
// Fix the import paths to stay within src directory
import { userStore } from '../../store/userStore';
import RefreshContext from '../../context/refreshContext';
import {
    useRequestMemoRecommendation,
    useHODRecommend,
    useReverseHODRecommend,
    useHOFRecommend,
    useReverseHOFRecommend,
    useHOURecommend,
    useReverseHOURecommend,
    useBursarApproval,
    useReverseBursarApproval,
    useVCApproval,
    useReverseVCApproval,
    useDeleteMemo,
    useUpdateMemo,
    useDownloadMemo,
    useGetMemoComments,
    useRequestComment,
    useReturnWithComment
} from '../../hooks/memo/memoQuery';
import NoApprovalsComponent from './NoApprovalsComponent';
import MemoComment from './MemoComment';
import RequestCommentModal from './RequestCommentModal';
import ReturnCommentModal from './ReturnCommentModal';

// Event bus for communicating with parent component
const eventBus = document;

function PreviewMemo({ memo }) {
    const [isViewed, setIsViewed] = useState(false);
    const [comments, setComments] = useState([]);
    const [commentsLoaded, setCommentsLoaded] = useState(false);
    const [activeTab, setActiveTab] = useState('details');
    const [showRequestCommentModal, setShowRequestCommentModal] = useState(false);
    const [showReturnCommentModal, setShowReturnCommentModal] = useState(false);

    const handleOpen = () => setIsViewed(true);
    const handleClose = () => setIsViewed(false);

    const { flag, setFlag } = useContext(RefreshContext);

    const requestRecommendation = useRequestMemoRecommendation();
    const hodRecommend = useHODRecommend();
    const hodReverseRecommend = useReverseHODRecommend();
    const hofRecommend = useHOFRecommend();
    const hofReverseRecommend = useReverseHOFRecommend();
    const houRecommend = useHOURecommend();
    const houReverseRecommend = useReverseHOURecommend();
    const bursarApproval = useBursarApproval();
    const bursarReverseApproval = useReverseBursarApproval();
    const vcApprove = useVCApproval();
    const vcReverseApprove = useReverseVCApproval();
    const getMemoComments = useGetMemoComments();
    const requestComment = useRequestComment();
    const returnWithComment = useReturnWithComment();

    const user = userStore(state => state.user);
    const deleteMemo = useDeleteMemo();
    const updateMemo = useUpdateMemo();
    const downloadMemo = useDownloadMemo();

    // Load memo comments when the modal is opened
    useEffect(() => {
        const loadComments = async () => {
            if (isViewed && !commentsLoaded) {
                try {
                    const response = await getMemoComments(memo.id);
                    setComments(response.memoComments || []);
                    setCommentsLoaded(true);
                } catch (error) {
                    console.error('Error loading comments:', error);
                    message.error('Failed to load comments');
                }
            }
        };

        loadComments();
    }, [isViewed, commentsLoaded, memo.id, getMemoComments]);

    // Reset comments loaded state when modal closes
    useEffect(() => {
        if (!isViewed) {
            setCommentsLoaded(false);
        }
    }, [isViewed]);

    // dispatching the add event to our message bus
    const dispatchEditEvent = () => {
        handleClose();
        // return
        const addEvent = new CustomEvent("custom:edit", { detail: { memo: memo } });
        // console.log(`Emitting event: ${addEvent.type} ${JSON.stringify(addEvent.detail)}`);
        eventBus.dispatchEvent(addEvent);
    }

    const onUpdate = (d) => {
        // return
        const addEvent = new CustomEvent("custom:update", { detail: { memo: d } });
        // console.log(`Emitting event: ${addEvent.type} ${JSON.stringify(addEvent.detail)}`);
        eventBus.dispatchEvent(addEvent);
    }

    const previewDocument = async () => {
        await downloadMemo(memo.id);
    }

    const handleHODRecommendation = async () => {
        const response = await hodRecommend(memo.id);
        message.success("Memo Recommended successfully");
        memo = response.data
        setFlag(!flag)
        handleClose();
        onUpdate(response)
    }

    const handleHODReverseRecommendation = async () => {
        const response = await hodReverseRecommend(memo.id);
        message.success("Memo Reverse-Recommended successfully");
        memo = response.data
        setFlag(!flag)
        handleClose();
        onUpdate(response)
    }

    const handleHOURecommendation = async () => {
        const response = await houRecommend(memo.id);
        message.success("Memo Recommended successfully");
        memo = response.data
        setFlag(!flag)
        handleClose();
        onUpdate(response)
    }

    const handleRequestRecommendation = async () => {
        const response = await requestRecommendation(memo.id);
        message.success("Memo Submitted for Recommendation");
        memo = response.data
        setFlag(!flag)
        handleClose();
        onUpdate(response)
    }

    const handleBursarApproval = async () => {
        const response = await bursarApproval(memo.id);
        message.success("Memo Approved successfully");
        memo = response.data
        setFlag(!flag)
        handleClose();
        onUpdate(response)
    }

    const handleBursarReverseApproval = async () => {
        const response = await bursarReverseApproval(memo.id);
        message.success("Memo Reverse-Approved successfully");
        memo = response.data
        setFlag(!flag)
        handleClose();
        onUpdate(response)
    }

    const handleVCApproval = async () => {
        const response = await vcApprove(memo.id);
        // console.log("vc")
        message.success("Memo Approved successfully");
        memo = response.data
        setFlag(!flag)
        handleClose();
        onUpdate(response)
    }

    const handleVCReverseApproval = async () => {
        const response = await vcReverseApprove(memo.id);
        message.success("Memo Reverse-Approved successfully");
        memo = response.data
        setFlag(!flag)
        handleClose();
        onUpdate(response)
    }

    const handleHOUReverseRecommendation = async () => {
        const response = await houReverseRecommend(memo.id);
        message.success("Recommendation Requested Successfully");
        memo = response.data
        setFlag(!flag)
        handleClose();
        onUpdate(response)
    }

    // Handlers for new recommendation request functionality
    const handleRequestCommentClick = () => {
        setShowRequestCommentModal(true);
    };

    const handleRequestCommentSuccess = (response) => {
        setFlag(!flag);
        handleClose();
        onUpdate(response);
    };

    const handleReturnCommentClick = () => {
        setShowReturnCommentModal(true);
    };

    const handleReturnCommentSuccess = (response) => {
        setFlag(!flag);
        handleClose();
        onUpdate(response);
    };

    const handleSubmit = async () => {
        const payload = {}
        const { message: response } = await updateMemo(payload);
        message.success(response ?? "memo updated successfully");
        setIsViewed(false);
        setFlag(!flag);
        onUpdate(response)
    }

    const url = process.env.REACT_APP_SERVER_URL || ""

    const updateContent = (mem) => {
        const sig = "<img src=\"" + url + "/signature/fetch/"+ mem?.owner + "\" alt=\"signature\" width=\"120px\" height=\"80px\"/>";
        const t = mem.content?.replaceAll("{signature}", sig)
        return t;
    }

    const confirmDelete = async () => {
        const { message: response } = await deleteMemo(memo.id);
        message.success(response ?? "memo deleted successfully");
        setFlag(!flag);
        onUpdate()
    }

    const doApproval = (role) => {
        let approval;

        switch (role) {
            case "HEAD_OF_UNIT":
                approval = handleHOURecommendation;
                break;

            case "HEAD_OF_DEPARTMENT":
                approval = handleHODRecommendation;
                break;

            default:
                break;
        }

        return approval;
    }

    const doReverseApproval = (role) => {
        let approval;

        switch (role) {
            case "HEAD_OF_UNIT":
                approval = handleHOUReverseRecommendation;
                break;

            case "HEAD_OF_DEPARTMENT":
                approval = handleHODReverseRecommendation;
                break;

            default:
                break;
        }

        return approval;
    }

    const ApprovedRoles = [
        {
            role: "Head Of Unit",
            approved: memo.houRecommend,
            present: memo.memoScope !== 'DEPARTMENT'
        },
        {
            role: "Head Of Department",
            approved: memo.hodRecommend,
            present: memo.memoBoundary !== "UNIT"
        },
    ]

    // Status badges
    const getStatusBadge = (status) => {
        return status ? (
            <Tag color="success" style={{ borderRadius: '12px', padding: '2px 10px' }}>
                <CheckOutlined style={{ marginRight: '4px' }} />
                Approved
            </Tag>
        ) : (
            <Tag color="error" style={{ borderRadius: '12px', padding: '2px 10px' }}>
                <CloseCircleOutlined style={{ marginRight: '4px' }} />
                Not Approved
            </Tag>
        );
    }

    // Format the approval list items
    const renderApprovalItem = (item, index) => (
        <List.Item key={index} style={{ padding: '8px 0' }}>
            <Row style={{ width: '100%', alignItems: 'center' }}>
                <Col span={16}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{
                            width: '24px',
                            height: '24px',
                            backgroundColor: '#1890ff',
                            color: 'white',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: '10px'
                        }}>
                            {index + 1}
                        </div>
                        <span style={{ fontWeight: '500' }}>{item.role}</span>
                    </div>
                </Col>
                <Col span={8} style={{ textAlign: 'right' }}>
                    {getStatusBadge(item.approved)}
                </Col>
            </Row>
        </List.Item>
    );

    // Define the tabs for the modal
    const items = [
        {
            key: 'details',
            label: (
                <span>
                    <EyeOutlined style={{ marginRight: '8px' }} />
                    Details
                </span>
            ),
            children: (
                <div>
                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <Card
                                title={
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <div style={{
                                            width: '36px',
                                            height: '36px',
                                            backgroundColor: '#f0f5ff',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            marginRight: '10px'
                                        }}>
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M18 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V4C20 2.9 19.1 2 18 2ZM6 4H11V12L8.5 10.5L6 12V4Z" fill="#1890ff"/>
                                            </svg>
                                        </div>
                                        <span style={{ fontWeight: 'bold' }}>{memo.title}</span>
                                    </div>
                                }
                                bordered={false}
                                style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}
                            >
                                <Row gutter={[16, 16]}>
                                    <Col span={8}>
                                        <div style={{ marginBottom: '12px' }}>
                                            <div style={{ color: '#8c8c8c', fontSize: '13px', marginBottom: '4px' }}>Creator</div>
                                            <div style={{ fontWeight: '500' }}>{memo.creator?.name}</div>
                                        </div>
                                    </Col>
                                    <Col span={8}>
                                        <div style={{ marginBottom: '12px' }}>
                                            <div style={{ color: '#8c8c8c', fontSize: '13px', marginBottom: '4px' }}>Financial Year</div>
                                            <div style={{ fontWeight: '500' }}>{memo.financialYear?.value}</div>
                                        </div>
                                    </Col>
                                    <Col span={8}>
                                        <div style={{ marginBottom: '12px' }}>
                                            <div style={{ color: '#8c8c8c', fontSize: '13px', marginBottom: '4px' }}>Memo Type</div>
                                            <Tag color={memo.memoType === "CLAIM" ? "blue" : "green"} style={{ fontWeight: '500' }}>
                                                {memo.userMemoType || "N/A"}
                                            </Tag>
                                        </div>
                                    </Col>
                                    <Col span={12}>
                                        <div style={{ marginBottom: '12px' }}>
                                            <div style={{ color: '#8c8c8c', fontSize: '13px', marginBottom: '4px' }}>Next Approval</div>
                                            <Tag color="gold" style={{ borderRadius: '12px', padding: '2px 10px' }}>
                                                {memo.nextApprovalRole ?? "None"}
                                            </Tag>
                                        </div>
                                    </Col>
                                    <Col span={12}>
                                        {memo.tmpSerial && !memo.memoReference && (
                                            <div style={{ marginBottom: '12px' }}>
                                                <div style={{ color: '#8c8c8c', fontSize: '13px', marginBottom: '4px' }}>Serial</div>
                                                <div style={{ fontWeight: '500' }}>{memo.tmpSerial}</div>
                                            </div>
                                        )}
                                        {memo.memoReference && (
                                            <div style={{ marginBottom: '12px' }}>
                                                <div style={{ color: '#8c8c8c', fontSize: '13px', marginBottom: '4px' }}>Reference</div>
                                                <div style={{ fontWeight: '500', fontStyle: 'italic' }}>{memo.memoReference}</div>
                                            </div>
                                        )}
                                    </Col>
                                </Row>
                            </Card>
                        </Col>

                        <Col span={24}>
                            <Card
                                title={
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <div style={{
                                            width: '36px',
                                            height: '36px',
                                            backgroundColor: '#f6ffed',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            marginRight: '10px'
                                        }}>
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM9 17H7V10H9V17ZM13 17H11V7H13V17ZM17 17H15V13H17V17Z" fill="#52c41a"/>
                                            </svg>
                                        </div>
                                        <span>Payment Voucher Status</span>
                                    </div>
                                }
                                bordered={false}
                                style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}
                            >
                                <Row gutter={[16, 16]}>
                                    <Col span={12}>
                                        <div style={{ marginBottom: '12px' }}>
                                            <div style={{ color: '#8c8c8c', fontSize: '13px', marginBottom: '4px' }}>Approval Status</div>
                                            <div>
                                                {memo.paymentVoucher ?
                                                    <Tag color="processing" style={{ borderRadius: '12px', padding: '2px 10px' }}>
                                                        {memo.paymentVoucher?.approvedBy[memo.paymentVoucher.approvedBy.length - 1]}
                                                    </Tag> :
                                                    <Tag color="warning" style={{ borderRadius: '12px', padding: '2px 10px' }}>
                                                        No Approval Yet
                                                    </Tag>
                                                }
                                            </div>
                                        </div>
                                    </Col>
                                    <Col span={12}>
                                        <div style={{ marginBottom: '12px' }}>
                                            <div style={{ color: '#8c8c8c', fontSize: '13px', marginBottom: '4px' }}>Settlement Status</div>
                                            <div>
                                                {memo.paymentVoucher?.settled ?
                                                    <Tag color="success" style={{ borderRadius: '12px', padding: '2px 10px' }}>
                                                        <CheckOutlined style={{ marginRight: '4px' }} />
                                                        Voucher Settled
                                                    </Tag> :
                                                    <Tag color="warning" style={{ borderRadius: '12px', padding: '2px 10px' }}>
                                                        <CloseCircleOutlined style={{ marginRight: '4px' }} />
                                                        Not Settled
                                                    </Tag>
                                                }
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </Card>
                        </Col>

                        <Col span={24}>
                            <Card
                                title={
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <div style={{
                                            width: '36px',
                                            height: '36px',
                                            backgroundColor: '#e6f7ff',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            marginRight: '10px'
                                        }}>
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M12 2L4 5V11.09C4 16.14 7.41 20.85 12 22C16.59 20.85 20 16.14 20 11.09V5L12 2ZM16 13.04L14.59 14.45L10 9.86V16H8V8H10V9.86L13.79 13.65L16 11.44V13.04Z" fill="#1890ff"/>
                                            </svg>
                                        </div>
                                        <span>Recommendations</span>
                                    </div>
                                }
                                bordered={false}
                                style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}
                            >
                                <List
                                    dataSource={ApprovedRoles.filter(e => e.present)}
                                    locale={{
                                        emptyText: <NoApprovalsComponent />
                                    }}
                                    renderItem={renderApprovalItem}
                                    split={true}
                                />
                            </Card>
                        </Col>

                        {memo.comment && (
                            <Col span={24}>
                                <Alert
                                    message={memo.comment}
                                    type="info"
                                    showIcon
                                    style={{
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                        borderRadius: '4px'
                                    }}
                                />
                            </Col>
                        )}
                    </Row>

                    {/* Floating Comment Box - Latest Comment */}
                    {comments.length > 0 && (
                        <>


                            <div style={{
                                position: 'fixed',
                                bottom: '20px',
                                right: '20px',
                                width: '320px',
                                backgroundColor: '#fff',
                                border: '1px solid #d9d9d9',
                                borderRadius: '8px',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                zIndex: 1000,
                                padding: '16px',
                                maxHeight: '200px',
                                overflow: 'hidden',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease'
                            }}
                            onClick={() => setActiveTab('comments')}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.2)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                            }}
                            >
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    marginBottom: '12px',
                                    paddingBottom: '8px',
                                    borderBottom: '1px solid #f0f0f0'
                                }}>
                                    <CommentOutlined style={{
                                        color: '#1890ff',
                                        fontSize: '16px',
                                        marginRight: '8px'
                                    }} />
                                    <span style={{
                                        fontWeight: '600',
                                        color: '#262626',
                                        fontSize: '14px'
                                    }}>
                                        Latest Comment
                                    </span>
                                    <Badge
                                        count={comments.length}
                                        style={{ marginLeft: 'auto' }}
                                        showZero
                                    />
                                    <div style={{
                                        marginLeft: '8px',
                                        fontSize: '12px',
                                        color: '#1890ff',
                                        fontWeight: '500'
                                    }}>
                                        Click to view all →
                                    </div>
                                </div>
                                <div style={{
                                    fontSize: '13px',
                                    color: '#595959',
                                    lineHeight: '1.5',
                                    marginBottom: '8px',
                                    maxHeight: '80px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}>
                                    {comments[comments.length - 1]?.content || 'No comment text'}
                                </div>
                                <div style={{
                                    fontSize: '12px',
                                    color: '#8c8c8c',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}>
                                    <span>
                                        By: {comments[comments.length - 1]?.sentBy?.name || 'Unknown'}
                                    </span>
                                    <span>
                                        {comments[comments.length - 1]?.dateString ?
                                            comments[comments.length - 1].dateString :
                                            'Unknown date'
                                        }
                                    </span>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            ),
        },
        {
            key: 'content',
            label: (
                <span>
                    <FilePdfOutlined style={{ marginRight: '8px' }} />
                    Content
                </span>
            ),
            children: (
                <div style={{
                    padding: '16px',
                    background: '#fff',
                    borderRadius: '8px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}>
                    <div
                        id="memo-content"
                        dangerouslySetInnerHTML={{__html: updateContent(memo)}}
                        style={{
                            padding: '16px',
                            borderRadius: '4px',
                            border: '1px solid #f0f0f0',
                            background: '#fafafa'
                        }}
                    />
                </div>
            ),
        },
        {
            key: 'comments',
            label: (
                <span>
                    <CommentOutlined style={{ marginRight: '8px' }} />
                    Comments
                    <Badge
                        count={comments.length}
                        style={{ marginLeft: '8px' }}
                        showZero
                    />
                </span>
            ),
            children: (
                <div style={{ padding: '16px 0' }}>
                    {memo.submitted && (
                        <MemoComment
                            memo={memo}
                            comments={comments}
                            onCommentsChange={setComments}
                        />
                    )}
                </div>
            ),
        },
    ];

    // Action buttons to display in the modal footer
    const renderActionButtons = () => (
        <Space size={'small'} wrap>
            {user.userId === memo.creator.userId && !memo.submitted && (
                <>
                    <Button
                        type="primary"
                        onClick={() => dispatchEditEvent()}
                        icon={<EditOutlined />}
                    >
                        Edit
                    </Button>

                    {!memo.submitted && (
                        <Button
                            type='primary'
                            style={{ backgroundColor: 'green' }}
                            onClick={handleRequestRecommendation}
                            icon={<CheckOutlined />}
                        >
                            Submit
                        </Button>
                    )}
                </>
            )}

            <Button
                type="primary"
                style={{ backgroundColor: 'green' }}
                onClick={previewDocument}
                icon={<FilePdfOutlined />}
            >
                Preview
            </Button>

            {/* Head of unit */}
            {memo.submitted && !memo.houRecommend && (user.roles?.includes(memo.nextApprovalRole)) && (
                <>
                    <Button
                        type="primary"
                        onClick={doApproval(memo.nextApprovalRole)}
                        icon={<SendOutlined />}
                    >
                        Recommend as head of unit
                    </Button>

                    <Button
                        type="primary"
                        danger
                        onClick={doReverseApproval(memo.nextApprovalRole)}
                        icon={<CloseCircleOutlined />}
                    >
                        Discommend
                    </Button>

                    {!memo.isAwaitingCommentRequest &&
                        <Button
                            type="primary"
                            onClick={handleRequestCommentClick}
                            icon={<CommentOutlined />}
                        >
                            Request Comment
                        </Button>
                    }
                </>
            )}

            {/* Head of dept */}
            {memo.submitted && memo.houRecommend && !memo.hodRecommend && (user.roles?.includes(memo.nextApprovalRole)) && (
                <>
                    <Button
                        type="primary"
                        onClick={doApproval(memo.nextApprovalRole)}
                        icon={<SendOutlined />}
                    >
                        Recommend as head of department
                    </Button>

                    <Button
                        type="primary"
                        danger
                        onClick={doReverseApproval(memo.nextApprovalRole)}
                        icon={<CloseCircleOutlined />}
                    >
                        Discommend
                    </Button>

                    {!memo.isAwaitingCommentRequest &&
                        <Button
                            type="primary"
                            onClick={handleRequestCommentClick}
                            icon={<CommentOutlined />}
                        >
                            Request Comment
                        </Button>
                    }
                </>
            )}
            {memo.submitted && !memo.approved && memo.vcApproval && (user.roles?.includes("BURSAR")) && (
                <>
                    <Button
                        type="primary"
                        onClick={handleBursarApproval}
                        icon={<SendOutlined />}
                    >
                        Recommend for payment
                    </Button>

                    <Button
                        type="primary"
                        danger
                        onClick={handleBursarReverseApproval}
                        icon={<CloseCircleOutlined />}
                    >
                        Reverse Approval
                    </Button>

                    {!memo.isAwaitingCommentRequest &&
                        <Button
                            type="primary"
                            onClick={handleRequestCommentClick}
                            icon={<CommentOutlined />}
                        >
                            Request Comment
                        </Button>
                    }
                </>
            )}

            {memo.submitted && !memo.vcApproval && memo.hodRecommend && (user.roles?.includes("VC")) && (
                <>
                    <Button
                        type="primary"
                        onClick={handleVCApproval}
                        icon={<SendOutlined />}
                    >
                        Approve as VC
                    </Button>

                    <Button
                        type="primary"
                        danger
                        onClick={handleVCReverseApproval}
                        icon={<CloseCircleOutlined />}
                    >
                        Reverse Approval
                    </Button>

                    {!memo.isAwaitingCommentRequest &&
                        <Button
                            type="primary"
                            onClick={handleRequestCommentClick}
                            icon={<CommentOutlined />}
                        >
                            Request Comment
                        </Button>
                    }
                </>
            )}

            {/* Show return with comment button for users who have pending comment requests */}
            {memo.isAwaitingCommentRequest && memo.awaitingCommentFromUser?.userId === user.userId && (
                <Button
                    type="primary"
                    onClick={handleReturnCommentClick}
                    icon={<CommentOutlined />}
                    style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                >
                    Return with Comment
                </Button>
            )}
        </Space>
    );

    return (
        <>
            <Row gutter={[8, 8]}>
                <Col span={12}>
                    <Button
                        type="primary"
                        style={{
                            backgroundColor: "#1890ff",
                            borderRadius: "4px",
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '36px'
                        }}
                        onClick={handleOpen}
                        icon={<EyeOutlined />}
                    >

                    </Button>
                </Col>
                <Col span={12}>
                    <Popconfirm
                        title="Delete this Memo"
                        description="Are you sure to delete this memo?"
                        onConfirm={confirmDelete}
                        onCancel={() => null}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            type="primary"
                            danger
                            disabled={memo.submitted}
                            style={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '36px',
                                borderRadius: "4px"
                            }}
                            icon={<DeleteOutlined />}
                        >

                        </Button>
                    </Popconfirm>
                </Col>
            </Row>

            <Modal
                centered
                title={
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px' }}>
                            <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2ZM16 18H8V16H16V18ZM16 14H8V12H16V14ZM13 9V3.5L18.5 9H13Z" fill="#1890ff"/>
                        </svg>
                        <span style={{ fontWeight: '500' }}>Memo: {memo.title}</span>
                    </div>
                }
                open={isViewed}
                onCancel={handleClose}
                footer={renderActionButtons()}
                width={800}
                bodyStyle={{ padding: '0px' }}
            >
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'start', fontWeight: '500', fontSize: '12pt', marginLeft: '.4em', marginBottom: '.4em' }}>By: {memo?.creator?.name}</span>
                <div style={{ maxHeight: "75vh", overflowY: "auto" }}>
                    <div className="tabs-container">
                        <Tabs
                            activeKey={activeTab}
                            onChange={setActiveTab}
                            items={items}
                            tabBarStyle={{
                                margin: '0',
                                padding: '0 16px',
                                backgroundColor: '#f5f5f5',
                                borderBottom: '1px solid #f0f0f0'
                            }}
                            style={{
                                padding: 0,
                                backgroundColor: '#fff'
                            }}
                        />
                    </div>
                </div>
            </Modal>

            {/* Request Comment Modal */}
            <RequestCommentModal
                visible={showRequestCommentModal}
                onClose={() => setShowRequestCommentModal(false)}
                memo={memo}
                onSuccess={handleRequestCommentSuccess}
            />

            {/* Return Comment Modal */}
            <ReturnCommentModal
                visible={showReturnCommentModal}
                onClose={() => setShowReturnCommentModal(false)}
                memo={memo}
                onSuccess={handleReturnCommentSuccess}
            />
        </>
    );
}
export default PreviewMemo;
